import { useReduxState } from 'hooks';
import { Button, RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { CheckboxInput } from 'components/common/form/common';
import { EditorBlockTranslationReference } from 'components/editor';
import { TextEditor } from 'components/editor/inputs';

interface Props {
  disableCorrectChange?: boolean;
  correct: boolean;
  deleteIsDisabled: boolean;
  feedback: string;
  feedbackTranslationReference?: string;
  id: string;
  index: number;
  isAIPreview?: boolean;
  isPreview?: boolean;
  isSingleChoice: boolean;
  onCorrectChange: (index: number, value: boolean) => void;
  onDelete: (index: number) => void;
  onFeedbackChange: (index: number, feedback: string) => void;
  onTextChange: (index: number, text: string) => void;
  showFeedbackEditor: boolean;
  text: string;
}

const EditorBlockChoiceTasksOption = ({
  disableCorrectChange,
  correct,
  deleteIsDisabled,
  feedback,
  feedbackTranslationReference,
  id,
  index,
  isAIPreview,
  isPreview,
  isSingleChoice,
  onCorrectChange,
  onDelete,
  onFeedbackChange,
  onTextChange,
  showFeedbackEditor,
  text
}: Props) => {
  const { messages, theme, serverConfig } = useReduxState();

  const handleCorrectChange = (ev) => {
    const value = ev.target.checked;
    if (correct !== value) onCorrectChange(index, value);
  };

  const handleDelete = () => {
    onDelete(index);
  };

  const handleFeedbackChange = (feedback: string) => {
    onFeedbackChange(index, feedback);
  };

  const handleTextChange = (text: string) => {
    onTextChange(index, text);
  };

  return (
    <div className="option" id={id}>
      <div className="content">
        <div className="correct">
          <CheckboxInput
            aria-label={messages.editorBlockMultipleChoiceOptionCorrect}
            checked={correct}
            disabled={isAIPreview || isPreview || disableCorrectChange}
            iconStyle={isSingleChoice ? { borderRadius: 50 } : undefined}
            id={`${id}-correct`}
            labelProps={{
              'data-tooltip': messages.editorBlockMultipleChoiceOptionCorrect
            }}
            onChange={handleCorrectChange}
          />
        </div>

        <div className="text">
          <div className="text-content">
            {isAIPreview || isPreview ? (
              <RichTextValue html={text} style={{ padding: 5 }} />
            ) : (
              <TextEditor
                ariaLabel={messages.editorBlockMultipleChoiceOption}
                content={text}
                onChange={handleTextChange}
                padding="5px"
                placeholder={messages.commonAddText}
                showBorder={false}
              />
            )}
          </div>
        </div>

        {!isAIPreview ? (
          <div className="delete">
            <Button
              aria-label={messages.commonDeleteOption}
              data-tooltip={messages.commonDeleteOption}
              category="lighter"
              disabled={isPreview || deleteIsDisabled}
              icon="trash-alt"
              onClick={handleDelete}
              size="small"
            />
          </div>
        ) : null}
      </div>

      {showFeedbackEditor ? (
        <div className="feedback">
          <div
            className="feedback-icon"
            data-tooltip={messages.editorBlockMultipleChoiceOptionFeedback}
          >
            <Icon icon="comment-alt" />
          </div>
          <div className="feedback-text">
            {isAIPreview || isPreview ? (
              <RichTextValue html={feedback} style={{ padding: 5 }} />
            ) : (
              <EditorBlockTranslationReference
                backgroundColor={theme.lightGrey}
                referenceHtml={feedbackTranslationReference}
                isInnerElement={true}
                style={{ margin: 5 }}
                translationHtml={feedback}
              >
                <TextEditor
                  ariaLabel={messages.editorBlockMultipleChoiceOptionFeedback}
                  backgroundColor="inherit"
                  colorPickerEnabled={
                    serverConfig.multipleChoiceUseAltFeedbackStyle
                  }
                  content={feedback}
                  onChange={handleFeedbackChange}
                  padding="5px"
                  placeholder={messages.commonFeedbackPlaceholder}
                  showBorder={false}
                />
              </EditorBlockTranslationReference>
            )}
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .option {
          background-color: white;
          border: 1px solid transparent;
          border-radius: ${theme.inputBorderRadius};
          padding: 5px 10px;
          transition: border-color 0.3s;
        }
        .option:focus-within {
          border-color: #a1a8aa;
        }
        .option:not(:first-child) {
          margin-top: 8px;
        }
        .content {
          align-items: top;
          display: flex;
          position: relative;
        }
        .correct {
          flex-grow: 0;
          flex-shrink: 0;
          margin-right: 10px;
          margin-top: 5px;
        }
        .text {
          align-items: top;
          display: flex;
          flex-grow: 1;
          flex-shrink: 1;
          font-size: 1.5rem;
          min-width: 0;
        }
        .text-content {
          flex-grow: 1;
          min-width: 0;
        }
        .text::before {
          counter-increment: multiple-choice-options;
          content: counter(multiple-choice-options, lower-alpha) ')';
          display: inline-block;
          font-weight: 400;
          margin-top: 5px;
        }
        .feedback-toggle {
          margin-left: 10px;
          margin-top: 1px;
        }
        .delete {
          flex-grow: 0;
          flex-shrink: 0;
          margin-left: 10px;
          margin-top: 1px;
        }
        .feedback {
          align-items: top;
          background-color: ${theme.lightGrey};
          border: 1px solid transparent;
          border-radius: ${theme.inputBorderRadius};
          display: flex;
          font-size: 1.4rem;
          margin: 8px 0 2px;
          padding: 0 8px;
          transition: border-color 0.3s;
        }
        .feedback:focus-within {
          border-color: #a1a8aa;
        }
        .feedback-icon {
          color: ${theme.textColorLighter};
          font-size: 1.1rem;
          margin-right: 2px;
          margin-top: 8px;
        }
        .feedback-text {
          flex-grow: 1;
          min-width: 0;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockChoiceTasksOption;
